// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Upload, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export default function RLHoopsTeamsManager() {
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState<string | null>(null);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('rlhoops_team_rankings')
        .select('*')
        .order('team_name');

      if (searchTerm.trim()) {
        query = query.ilike('team_name', `%${searchTerm.trim()}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Deduplicate by team_name to show unique teams
      const seen = new Map<string, any>();
      (data || []).forEach((t: any) => {
        const key = t.team_name.toLowerCase();
        if (!seen.has(key)) {
          seen.set(key, t);
        } else {
          const existing = seen.get(key);
          if (!existing.logo_url && t.logo_url) {
            seen.set(key, t);
          }
        }
      });
      setTeams(Array.from(seen.values()));
    } catch {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleLogoUpload = async (teamName: string, file: File) => {
    setUploading(teamName);
    try {
      const ext = file.name.split('.').pop();
      const path = `team-logos/${teamName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('rlhoops-assets')
        .upload(path, file, { upsert: true });

      if (uploadError) {
        if (uploadError.message?.includes('not found')) {
          toast.error('Storage bucket "rlhoops-assets" not found. Please create it in Supabase.');
          return;
        }
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from('rlhoops-assets')
        .getPublicUrl(path);

      const { error: updateError } = await supabase
        .from('rlhoops_team_rankings')
        .update({ logo_url: urlData.publicUrl })
        .ilike('team_name', teamName);

      if (updateError) throw updateError;

      toast.success(`Logo updated for ${teamName}`);
      loadTeams();
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setUploading(null);
    }
  };

  const handleRemoveLogo = async (teamName: string) => {
    try {
      const { error } = await supabase
        .from('rlhoops_team_rankings')
        .update({ logo_url: null })
        .ilike('team_name', teamName);
      if (error) throw error;
      toast.success('Logo removed');
      loadTeams();
    } catch {
      toast.error('Failed to remove logo');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Team Logo Management
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Upload logos for teams. When CSV results are imported, if the team name matches, the logo persists on public standings automatically.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Search teams..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : teams.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">No teams found. Import some results first.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Logo</TableHead>
                <TableHead>Team Name</TableHead>
                <TableHead>Age Group</TableHead>
                <TableHead className="text-center">Points</TableHead>
                <TableHead className="text-center">Record</TableHead>
                <TableHead className="w-[200px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.map((team: any) => (
                <TableRow key={team.id}>
                  <TableCell>
                    {team.logo_url ? (
                      <img src={team.logo_url} alt={team.team_name} className="h-10 w-10 rounded-full object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                        <Shield className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{team.team_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{team.age_group?.toUpperCase()}</Badge>
                  </TableCell>
                  <TableCell className="text-center font-bold">{team.total_points}</TableCell>
                  <TableCell className="text-center">{team.total_wins}W - {team.total_losses}L</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleLogoUpload(team.team_name, file);
                          }}
                        />
                        <Button variant="outline" size="sm" asChild disabled={uploading === team.team_name}>
                          <span>
                            {uploading === team.team_name ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Upload className="h-3 w-3 mr-1" />
                            )}
                            {team.logo_url ? 'Change' : 'Upload'}
                          </span>
                        </Button>
                      </label>
                      {team.logo_url && (
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveLogo(team.team_name)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
